import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { colorsPalette } from '../../../../../../shared/ui/styles/colors-palette';
import { useUILogic } from '../../../../shared/providers/UILogicProvider/hooks';
import {
  PHONE_CODE_LENGTH,
  RegisterValidationError,
} from '../../../../../infrastructure/types/features/auth/auth-types';
import { PhoneCode } from '../../PhoneCode';
import UiKit from '../../../../../../shared/ui/ui-kit';

let OutgoingConfirmation = function () {
  const { t } = useTranslation();
  const { authorizationUiModule } = useUILogic();
  const manager = authorizationUiModule.registerUIManager.coreManager.outgoingManager;

  useEffect(() => {
    if (!manager.isWasRequest) {
      manager.requestVerification();
    }
  }, []);

  const handleCodeChange = useCallback(
    (code: string) => {
      manager.setPhoneCode(code as PhoneCode);
      if (code.length === PHONE_CODE_LENGTH) {
        manager.submitPhoneCode();
      }
    },
    [manager],
  );

  const handleRefresh = useCallback(() => {
    manager.requestVerification();
  }, [manager]);

  return (
    <OutgoingBlock>
      <CodeBlock>
        <PhoneCode onCodeChange={handleCodeChange} />
        {manager.codeError === 'invalid_code' && (
          <CodeError>{t(RegisterValidationError.PHONE_CODE_INVALID)}</CodeError>
        )}
      </CodeBlock>
      <CallAgain>
        {manager.confirmationSecondsLeft > 0 ? (
          <Trans
            i18nKey="register.phone.confirm.retry.timer"
            values={{ seconds: manager.confirmationSecondsLeft }}
            components={{
              u: <u />,
            }}
          />
        ) : (
          <Trans
            i18nKey="register.phone.confirm.callagain"
            components={{
              callButton: <UiKit.Button size="small" type="link" onClick={handleRefresh} />,
              u: <u />,
            }}
          />
        )}
      </CallAgain>
      {manager.verificationErrorKey && (
        <VerificationError>{t(manager.verificationErrorKey)}</VerificationError>
      )}
    </OutgoingBlock>
  );
};

const OutgoingBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CodeBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CodeError = styled.div`
  font-size: 12px;
  color: ${colorsPalette.dustyRed500};
  text-align: center;
`;

const CallAgain = styled.div`
  font-size: 12px;
  color: ${colorsPalette.gray600};
  text-align: center;
`;

const CallButton = styled.span`
  color: ${colorsPalette.gray600};
  cursor: pointer;
`;

const VerificationError = styled.div`
  font-size: 12px;
  color: ${colorsPalette.dustyRed500};
  text-align: center;
`;

OutgoingConfirmation = observer(OutgoingConfirmation);

export { OutgoingConfirmation };
